import React, { useState } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";import "../../styles/upload-programme-wizard.css";

const WIZARD_STEPS = [
  "Select File",
  "Preview",
  "Duplicate Review",
  "Import Summary"
];

export default function UploadProgrammeWizard({
  isOpen,
  onClose,
  existingProgrammes = []
}) {

  const fileInputRef = React.useRef(null);

  const [selectedFile, setSelectedFile] = React.useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState("");
  const [validationResults, setValidationResults] = useState([]);

  const handleBrowse = () => {
    fileInputRef.current?.click();
  };

  function normalizeProgrammeName(name = "") {

    return String(name)
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

}

function normalizeCellValue(value) {

    return value === null || value === undefined
        ? ""
        : String(value).trim();

}

function reviewRows(rows, existingProgrammes) {

    const existingNames = new Set(

        existingProgrammes.map(programme =>
            normalizeProgrammeName(programme?.program_name)
        )

    );

    const acceptedNames = new Set();

    return rows.map((row, index) => {

        const programName = normalizeCellValue(row.program_name);
        const degree = normalizeCellValue(row.degree);
        const applyUrl = normalizeCellValue(row.apply_url);
        const reasons = [];

        if (!programName) reasons.push("Programme name is required");
        if (!degree) reasons.push("Degree is required");
        if (!applyUrl) reasons.push("Application URL is required");

        const reviewedRow = {
            ...row,
            program_name: programName,
            degree,
            apply_url: applyUrl,
            rowNumber: index + 2,
            reasons
        };

        if (reasons.length > 0) {
            return {
                ...reviewedRow,
                disposition: "invalid"
            };
        }

        const normalizedName = normalizeProgrammeName(programName);

        if (existingNames.has(normalizedName)) {
            return {
                ...reviewedRow,
                disposition: "duplicate",
                reasons: ["Already exists in your programme catalogue"]
            };
        }

        if (acceptedNames.has(normalizedName)) {
            return {
                ...reviewedRow,
                disposition: "duplicate",
                reasons: ["Repeated in this uploaded file"]
            };
        }

        acceptedNames.add(normalizedName);

        return {
            ...reviewedRow,
            disposition: "accepted"
        };

    });

}

  const reviewCounts = validationResults.reduce((counts, row) => {

    counts[row.disposition] += 1;
    counts.total += 1;

    return counts;

  }, { accepted: 0, duplicate: 0, invalid: 0, total: 0 });

  const handleContinue = async () => {

        if (!selectedFile) return;

        if (currentStep === 4) {

            resetWizard();

            onClose();

            return;

        }

        if (currentStep === 2) {

            setCurrentStep(3);

            return;

        }

        if (currentStep === 3) {

            if (reviewCounts.accepted === 0) return;

            setCurrentStep(4);

            return;

        }

        setParseError("");

        const extension =
            selectedFile.name
                .split(".")
                .pop()
                .toLowerCase();

        try {

            if (extension === "csv") {

                Papa.parse(selectedFile, {

                    header: true,

                    skipEmptyLines: true,

                    complete: (results) => {

                        const reviewedRows =
                                reviewRows(
                                    results.data,
                                    existingProgrammes
                                );

                            setParsedRows(results.data);

                            setValidationResults(reviewedRows);

                            setCurrentStep(2);

                    },

                    error: () => {

                        setParseError("Unable to read CSV file.");

                    }

                });

                return;

            }

            if (extension === "xlsx") {

                const buffer =
                    await selectedFile.arrayBuffer();

                const workbook =
                    XLSX.read(buffer);

                const worksheet =
                    workbook.Sheets[
                        workbook.SheetNames[0]
                    ];

                const rows =
                    XLSX.utils.sheet_to_json(worksheet);

                const reviewedRows =
                    reviewRows(
                        rows,
                        existingProgrammes
                    );

                setParsedRows(rows);

                setValidationResults(reviewedRows);

                setCurrentStep(2);
            }

        }

        catch (error) {

            console.error(error);

            setParseError(
                "Unable to read selected file."
            );

        }

    };

  const handleFileSelected = (event) => {

    const file = event.target.files[0];

    if (!file) return;

    setSelectedFile(file);
    setCurrentStep(1);
    setParsedRows([]);
    setValidationResults([]);
    setParseError("");

  };

  const resetWizard = () => {

        setSelectedFile(null);

        setCurrentStep(1);

        setParsedRows([]);

        setValidationResults([]);

        setParseError("");

        if (fileInputRef.current) {

            fileInputRef.current.value = "";

        }

    };

  if (!isOpen) return null;

  return (

    <div className="admin-modal-overlay add-programme-modal-overlay">

      <div
        className="admin-modal add-programme-modal upload-programme-wizard"
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-programme-title"
      >

        <div className="admin-modal-header wizard-header">

          <div>

            <h2 id="upload-programme-title">Upload Programme Catalogue</h2>

            <p>
              Import multiple programmes using a CSV or Excel file.
            </p>

          </div>

          <button
            type="button"
            className="admin-modal-close"
            aria-label="Close Upload Programme Catalogue"
            onClick={() => {
              resetWizard();
              onClose();
            }}
          >
            ×
          </button>

        </div>

        <nav className="wizard-progress" aria-label="Upload programme progress">

          <ol>
            {WIZARD_STEPS.map((label, index) => {
              const step = index + 1;
              const stepState =
                step < currentStep
                  ? "completed"
                  : step === currentStep
                    ? "current"
                    : "upcoming";

              return (
                <li
                  key={label}
                  className={`wizard-progress-step is-${stepState}`}
                  aria-current={stepState === "current" ? "step" : undefined}
                >
                  <span className="wizard-progress-marker" aria-hidden="true">
                    {stepState === "completed" ? "✓" : step}
                  </span>
                  <span className="wizard-progress-label">
                    <span className="wizard-progress-state">
                      {stepState === "completed"
                        ? "Completed"
                        : stepState === "current"
                          ? "Current step"
                          : "Upcoming"}
                    </span>
                    {label}
                  </span>
                </li>
              );
            })}
          </ol>

        </nav>

        <div className="wizard-body">

            {currentStep === 1 && (

                <div className="upload-drop-zone">

                    <h3>

                        Choose a CSV or Excel file

                    </h3>

                    <p>

                        Select a file from your computer to preview the programmes.

                    </p>

                    <>

                        <input

                            ref={fileInputRef}

                            type="file"

                            accept=".csv,.xlsx"

                            hidden

                            onChange={handleFileSelected}

                        />

                        <button

                            type="button"

                            className="browse-files-btn"

                            onClick={handleBrowse}

                        >

                            Browse Files

                        </button>

                    </>

                    <span className="supported-files">
                        Accepted formats: .csv, .xlsx
                    </span>

                    {selectedFile && (

                        <div className="selected-file">

                            Selected File

                            <strong>

                                {selectedFile.name}

                            </strong>

                        </div>

                    )}

                    {parseError && (

                        <div className="upload-error">

                            {parseError}

                        </div>

                    )}

                </div>

            )}

            {currentStep === 2 && (

            <div className="upload-preview">

                <div className="preview-header">

                    <h3>

                        Preview Programmes

                    </h3>

                    <p>

                        {parsedRows.length} programme(s) detected.

                    </p>

                </div>

                <div className="preview-table-container">

                    <table className="preview-table">

                        <thead>

                            <tr>

                                <th>#</th>

                                <th>Programme Name</th>

                                <th>Degree</th>

                                <th>Apply URL</th>
                                <th>Validation</th>

                            </tr>

                        </thead>

                        <tbody>

                            {validationResults.map((row, index) => (

                                <tr key={index}>

                                    <td>{row.rowNumber}</td>

                                    <td>

                                        {row.program_name || "—"}

                                    </td>

                                    <td>

                                        {row.degree || "—"}

                                    </td>

                                    <td title={row.apply_url}>

                                        {row.apply_url
                                            ? row.apply_url.length > 45
                                                ? `${row.apply_url.substring(0,45)}...`
                                                : row.apply_url
                                            : "—"}

                                    </td>

                                    <td>

                                        <span
                                            className={
                                                `validation-${row.disposition}`
                                            }
                                        >

                                            {row.disposition === "accepted"
                                                ? "Accepted"
                                                : row.disposition === "duplicate"
                                                    ? "Duplicate"
                                                    : "Invalid"}

                                        </span>

                                    </td>

                                </tr>

                            ))}

                        </tbody>

                    </table>

                </div>

            </div>

        )}

        {currentStep === 3 && (

        <div className="upload-review">

            <div className="preview-header">

                <h3>Duplicate Review</h3>

                <p>Duplicate and invalid rows will not be imported.</p>

            </div>

            <div className="review-counts" aria-label="Catalogue review totals">

                <div className="review-count review-count-accepted">
                    <strong>{reviewCounts.accepted}</strong>
                    <span>Accepted</span>
                </div>

                <div className="review-count review-count-duplicate">
                    <strong>{reviewCounts.duplicate}</strong>
                    <span>Duplicates</span>
                </div>

                <div className="review-count review-count-invalid">
                    <strong>{reviewCounts.invalid}</strong>
                    <span>Invalid</span>
                </div>

                <div className="review-count">
                    <strong>{reviewCounts.total}</strong>
                    <span>Total rows</span>
                </div>

            </div>

            <div className="preview-table-container">

                <table className="preview-table review-table">

                    <thead>
                        <tr>
                            <th>Row</th>
                            <th>Programme Name</th>
                            <th>Degree</th>
                            <th>Disposition</th>
                            <th>Reason</th>
                        </tr>
                    </thead>

                    <tbody>
                        {validationResults.map((row, index) => (
                            <tr key={index}>
                                <td>{row.rowNumber}</td>
                                <td>{row.program_name || "—"}</td>
                                <td>{row.degree || "—"}</td>
                                <td>
                                    <span className={`validation-${row.disposition}`}>
                                        {row.disposition === "accepted"
                                            ? "Accepted"
                                            : row.disposition === "duplicate"
                                                ? "Duplicate"
                                                : "Invalid"}
                                    </span>
                                </td>
                                <td>{row.reasons.join("; ") || "—"}</td>
                            </tr>
                        ))}
                    </tbody>

                </table>

            </div>

            {reviewCounts.accepted === 0 && (
                <p className="review-blocked-message">
                    No valid new programmes are available to import.
                </p>
            )}

        </div>

    )}

    {currentStep === 4 && (

        <div className="upload-preview-placeholder">

            <h3>

                Import Summary

            </h3>

            <p>

                Bulk programme import will be implemented in a later milestone.

            </p>

        </div>

    )}

        </div>

        <div className="wizard-footer">

          <button
            type="button"
            className="secondary-btn"
            onClick={() => {

                resetWizard();

                onClose();

            }}
        >
            Cancel
        </button>

        <button
            type="button"
            className="primary-btn"
            disabled={
                !selectedFile ||
                (currentStep === 3 && reviewCounts.accepted === 0)
            }
            onClick={handleContinue}
        >

            {currentStep === 4
                ? "Finish"
                : "Continue"}

        </button>

        </div>

      </div>

    </div>

  );

}
